/**
 * Guide links — which guide chapters a page recommends at its end.
 *
 * Data: `src/data/components/guide-links.json` (route → 2–4 chapter refs plus a
 * contextual heading). Chapter facts (title, question, reading time, status)
 * come from the two guide catalogues, so a renamed chapter changes everywhere
 * at once and a chapter that is not `live` cannot be linked.
 *
 * Only the catalogues are imported here, never the chapter files: the resolver
 * must stay cheap enough for the few client pages that render the block
 * themselves (services hub, webinars). Server pages pass the rendered block
 * into their client templates as a slot instead.
 */

import guideLinks from "@data/components/guide-links.json";
import agenticHub from "@data/pages/guide/agentic-bi.json";
import migrationHub from "@data/pages/guide/power-bi-migration.json";

const AGENTIC_BASE_PATH = "/guide/agentic-bi";
const MIGRATION_BASE_PATH = "/guide/power-bi-migration";
const FILE = "components/guide-links.json";

type GuideSeries = "agentic-bi" | "power-bi-migration";

interface CatalogueItem {
  number: number;
  slug: string;
  title: string;
  question: string;
  readingTime: string;
  status: string;
}

interface RouteEntry {
  title: string;
  lede: string;
  chapters: string[];
}

export interface GuideLinkChapter {
  /** `<series>/<slug>` as written in the JSON. */
  ref: string;
  series: GuideSeries;
  slug: string;
  number: number;
  href: string;
  /** Chapter H1 as in the catalogue — used verbatim as anchor text. */
  title: string;
  /** The question the chapter answers; shown under the anchor. */
  question: string;
  readingTime: string;
  /** "Kapitel 04 · 9 Min." or "Migrations-Guide · 11 Min." */
  tag: string;
}

export interface ResolvedGuideLinks {
  route: string;
  eyebrow: string;
  title: string;
  /** Markdown links allowed (`[text](href)`); SectionHead converts them. */
  lede: string;
  chapters: GuideLinkChapter[];
  hub: { href: string; label: string };
  allChaptersLabel: string;
  readLabel: string;
  schemaName: string;
  /** Number of live Agentic chapters — the `{count}` placeholder. */
  liveCount: number;
}

const catalogues: Record<GuideSeries, { basePath: string; items: CatalogueItem[] }> = {
  "agentic-bi": {
    basePath: AGENTIC_BASE_PATH,
    items: (agenticHub as { chapters: { items: CatalogueItem[] } }).chapters.items,
  },
  "power-bi-migration": {
    basePath: MIGRATION_BASE_PATH,
    items: (migrationHub as { chapters: { items: CatalogueItem[] } }).chapters.items,
  },
};

function requireString(value: unknown, name: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`${FILE}: missing field \`${name}\``);
  }
  return value;
}

function liveAgenticCount(): number {
  return catalogues["agentic-bi"].items.filter((item) => item.status === "live").length;
}

function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match
  );
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Resolve one `<series>/<slug>` ref; throws unless the chapter is live. */
export function resolveGuideChapterRef(ref: string): GuideLinkChapter {
  const [series, slug, ...rest] = ref.split("/");
  if (!slug || rest.length > 0 || !(series in catalogues)) {
    throw new Error(`${FILE}: chapter ref "${ref}" must be "<agentic-bi|power-bi-migration>/<slug>"`);
  }
  const catalogue = catalogues[series as GuideSeries];
  const item = catalogue.items.find((entry) => entry.slug === slug);
  if (!item) {
    throw new Error(`${FILE}: chapter "${ref}" is not in the ${series} catalogue`);
  }
  if (item.status !== "live") {
    throw new Error(`${FILE}: chapter "${ref}" is "${item.status}", only live chapters may be linked`);
  }
  const labels = guideLinks.labels;
  const tag =
    series === "agentic-bi"
      ? fill(requireString(labels.chapterTag, "labels.chapterTag"), {
          number: pad(item.number),
          readingTime: item.readingTime,
        })
      : fill(requireString(labels.migrationTag, "labels.migrationTag"), {
          readingTime: item.readingTime,
        });
  return {
    ref,
    series: series as GuideSeries,
    slug,
    number: item.number,
    href: `${catalogue.basePath}/${slug}`,
    title: requireString(item.title, `${series}/${slug}.title`),
    question: requireString(item.question, `${series}/${slug}.question`),
    readingTime: requireString(item.readingTime, `${series}/${slug}.readingTime`),
    tag,
  };
}

function findEntry(route: string): RouteEntry {
  const routes = guideLinks.routes as Record<string, RouteEntry>;
  if (routes[route]) {
    return routes[route];
  }
  const prefixes = guideLinks.prefixes as Record<string, RouteEntry>;
  const prefix = Object.keys(prefixes)
    .filter((candidate) => route.startsWith(candidate))
    .sort((a, b) => b.length - a.length)[0];
  if (prefix) {
    return prefixes[prefix];
  }
  throw new Error(
    `${FILE}: no guide links for route "${route}" — add it to \`routes\` (or a prefix to \`prefixes\`)`
  );
}

/** Routes with an exact mapping (tests iterate over these). */
export function guideLinkRoutes(): string[] {
  return Object.keys(guideLinks.routes);
}

/** Prefix fallbacks (blog posts, webinars, success stories). */
export function guideLinkPrefixes(): string[] {
  return Object.keys(guideLinks.prefixes);
}

export function resolveGuideLinks(route: string): ResolvedGuideLinks {
  const entry = findEntry(route);
  const refs = entry.chapters;
  if (!Array.isArray(refs) || refs.length < 2 || refs.length > 4) {
    throw new Error(`${FILE}: route "${route}" needs 2–4 chapters, has ${refs?.length ?? 0}`);
  }
  if (new Set(refs).size !== refs.length) {
    throw new Error(`${FILE}: route "${route}" lists a chapter twice`);
  }
  const liveCount = liveAgenticCount();
  const values = { count: liveCount };
  const labels = guideLinks.labels;
  return {
    route,
    eyebrow: requireString(labels.eyebrow, "labels.eyebrow"),
    title: fill(requireString(entry.title, `routes["${route}"].title`), values),
    lede: fill(requireString(entry.lede, `routes["${route}"].lede`), values),
    chapters: refs.map(resolveGuideChapterRef),
    hub: {
      href: requireString(guideLinks.hub.href, "hub.href"),
      label: requireString(guideLinks.hub.label, "hub.label"),
    },
    allChaptersLabel: fill(requireString(labels.allChaptersLabel, "labels.allChaptersLabel"), values),
    readLabel: requireString(labels.readLabel, "labels.readLabel"),
    schemaName: requireString(labels.schemaName, "labels.schemaName"),
    liveCount,
  };
}
